/**
 * Single Active Session Manager
 * ----------------------------------------------------------------------------
 * Enforces "one active login per user": signing into the same account from a
 * new browser/device invalidates the previous browser's session.
 *
 * Cross-browser logout cannot be done frontend-only - Chrome and Edge isolate
 * their storage - so enforcement lives server-side:
 *   1. On successful login the app calls `registerSession(user)`, POSTing
 *      `{ user, session_id }` to `/session/register`. The backend stores
 *      `session:active:<user> = <session_id>` in Redis, OVERWRITING whatever
 *      was there before. "Last sign-in wins": the newest browser becomes the
 *      active session and the older one is automatically stale.
 *   2. Every API request carries `session_id` (see `attachSession`).
 *   3. The backend rejects requests whose session_id is no longer active with
 *      401 {"error": "SESSION_EXPIRED"}.
 *   4. On 401 SESSION_EXPIRED the app calls `handleSessionExpired()`, which
 *      sets a flag in sessionStorage and reloads the app. App.js consumes the
 *      flag on boot (`consumeSessionExpiredFlag`) and shows a
 *      "Signed In Elsewhere" screen instead of the dashboard.
 *
 * sessionStorage (not a module variable) is used for the session id so every
 * tab of one browser shares a single registered session, while a different
 * browser (isolated storage) gets its own id - exactly what the server-side
 * check in the backend compares against.
 */

import { localConfig } from "../config/localConfig";

const SESSION_ID_KEY = "cam_copilot_session_id";
const SESSION_USER_KEY = "cam_copilot_session_user";
const SESSION_EXPIRED_FLAG = "cam_copilot_session_expired";

/** Generate a random, unique session id for this browser/tab. */
export function generateSessionID() {
  try {
    if (
      typeof crypto !== "undefined" &&
      typeof crypto.randomUUID === "function"
    ) {
      return crypto.randomUUID();
    }
  } catch (e) {
    // fall through to manual generation
  }
  const randomPart =
    Math.random().toString(36).substring(2, 12) +
    Math.random().toString(36).substring(2, 12);
  return `${randomPart}_${Date.now()}`;
}

/**
 * Return the stable session id for THIS tab/browser. The id is created once
 * and persisted in sessionStorage, so reloads keep the same identity but a
 * different browser (different storage) gets a fresh id.
 */
export function getSessionId() {
  try {
    let sessionId = sessionStorage.getItem(SESSION_ID_KEY);
    if (!sessionId) {
      sessionId = generateSessionID();
      sessionStorage.setItem(SESSION_ID_KEY, sessionId);
    }
    return sessionId;
  } catch (e) {
    // sessionStorage unavailable (rare embedded contexts) - fall back to a
    // per-page-load value so session handling still works for this load.
    if (!window.__camCopilotSessionId) {
      window.__camCopilotSessionId = generateSessionID();
    }
    return window.__camCopilotSessionId;
  }
}

// Backward-compatible module-level session id (previously exported from
// App.js). New code should call getSessionId() instead.
export const currentSessionID = getSessionId();

/**
 * Register this browser as the user's single active session. The backend
 * overwrites `session:active:<user>`, which is what invalidates the previous
 * browser. Best-effort: failures are logged and ignored (the enforced backend
 * routes fail open when this user has no registered session).
 */
export async function registerSession(user) {
  if (!user) {
    return;
  }
  try {
    sessionStorage.setItem(SESSION_USER_KEY, user);
  } catch (e) {
    // ignore persistence errors
  }
  try {
    const response = await fetch(`${localConfig.apiBaseUrl}/session/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user, session_id: getSessionId() }),
    });
    if (!response.ok) {
      console.warn("[SESSION] Register failed with status:", response.status);
    }
  } catch (e) {
    console.warn("[SESSION] Error registering session:", e);
  }
}

/**
 * Return the user whose session was most recently registered by THIS browser,
 * or "" when nobody has been registered yet.
 */
export function getRegisteredUser() {
  try {
    return sessionStorage.getItem(SESSION_USER_KEY) || "";
  } catch (e) {
    return "";
  }
}

/**
 * Remove the user's active session record server-side. Used on explicit logout
 * so the next sign-in gets a clean slate. NOTE: this signs out the single
 * active session globally (only one is ever active per user).
 */
export async function endSessionOnServer(user) {
  if (!user) {
    return;
  }
  try {
    await fetch(`${localConfig.apiBaseUrl}/session/logout`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user }),
    });
  } catch (e) {
    console.warn("[SESSION] Error notifying backend of logout:", e);
  }
}

/**
 * Attach this browser's session id to an outbound request payload so the
 * backend can enforce the single-active-session rule. The payload is mutated
 * and returned.
 */
export function attachSession(payload) {
  if (!payload) {
    payload = {};
  }
  if (!payload.session_id) {
    payload.session_id = getSessionId();
  }
  return payload;
}

/**
 * Detect the backend's session-expired response: HTTP 401 with
 * body.error === "SESSION_EXPIRED". Async because the body must be parsed.
 */
export async function isValidSessionError(response) {
  if (!response || response.status !== 401) {
    return false;
  }
  try {
    const body = await response.clone().json();
    return String(body?.error || "").toUpperCase() === "SESSION_EXPIRED";
  } catch (e) {
    return false;
  }
}

/** Store a flag that survives the reload, telling App.js to show the gate. */
export function setSessionExpiredFlag() {
  try {
    sessionStorage.setItem(SESSION_EXPIRED_FLAG, "1");
  } catch (e) {
    // ignore
  }
}

/** Read and clear the flag (checked once on app boot by App.js). */
export function consumeSessionExpiredFlag() {
  try {
    const flag = sessionStorage.getItem(SESSION_EXPIRED_FLAG);
    if (flag) {
      sessionStorage.removeItem(SESSION_EXPIRED_FLAG);
      return true;
    }
  } catch (e) {
    // ignore
  }
  return false;
}

/**
 * React to a 401 SESSION_EXPIRED from any enforced API: flag the session as
 * expired, then reload so App.js boots into the "Signed In Elsewhere" gate.
 */
export function handleSessionExpired() {
  console.warn(
    "[SESSION] This session is no longer active (same account signed in from another browser). Logging out."
  );
  setSessionExpiredFlag();
  try {
    window.location.reload();
  } catch (e) {
    // ignore
  }
}